<template>
    <md-dialog :md-active.sync="showDialog"
                class="upload-dialog"
                :md-click-outside-to-close="false">
        
        <md-dialog-title>
            Upload original files
        </md-dialog-title>

        <div class="dialog-content-wrapper" :class="{ 'is-double-sided': isDoubleSided }">
            <div class="upload-section">
                <div class="upload-dialog__header">
                    <span>{{ isDoubleSided ? 'Front side (Optional)' : 'Upload original' }}</span>
                    <input type="file" @change="showPreview($event, 'front')" ref="frontInput">
                    <md-button class="md-raised md-primary"
                        @click.native="$refs.frontInput.click()">Choose file</md-button>
                </div>
                
                <div class="upload-dialog__preview">
                    <img v-if="frontPreviewUrl"
                        :src="frontPreviewUrl"
                        class="preview-image"
                        alt="Front Preview">

                    <p class="md-body-1" v-if="!frontPreviewUrl">
                        {{ isDoubleSided ? 'Back preview will appear here' : 'Preview will appear here' }}
                    </p>
                </div>
            </div>

            <div class="upload-section" v-if="isDoubleSided">
                <div class="upload-dialog__header">
                    <span>Back side (Optional)</span>
                    <input type="file" @change="showPreview($event, 'back')" ref="backInput">
                    <md-button class="md-raised md-primary"
                        @click.native="$refs.backInput.click()">Choose file</md-button>
                </div>
                
                <div class="upload-dialog__preview">
                    <img v-if="backPreviewUrl"
                        :src="backPreviewUrl"
                        class="preview-image"
                        alt="Back Preview">

                    <p class="md-body-1" v-if="!backPreviewUrl">Back preview will appear here</p>
                </div>
            </div>
        </div>
        <div class="upload-dialog__note" v-if="frontPreviewUrl || backPreviewUrl">
            <AppInput
                controlType="textarea"
                placeholder="Note"
                rows="2"
                v-model="fileNote"/> 
        </div>
        <AppSpinner v-if="loading" :loading="loading" class="floating-spinner"/>

        <md-dialog-actions>
            <md-button class="md-primary" @click="closeDialog()">Cancel</md-button>
            <md-button  :disabled="!canSubmit"
                        class="md-primary upload-file-btn"
                        @click="uploadFile()">Finish</md-button>
        </md-dialog-actions>
        </md-dialog>
</template>

<script>
import OrderModel from '@/models/OrderModel';

export default {
    name: 'DesignDashboardUploadDialog',
    props: {
        showDialog: {
            type: Boolean,
            default: false
        },
        order: {
            type: OrderModel,
            default: null
        }
    },
    data: () => ({
        frontPreviewUrl: '',
        backPreviewUrl: '',
        frontFile: null,
        backFile: null,
        fileNote: '',
        loading: false
    }),
    computed: {
        isDoubleSided() {
            return this.order && this.order.adspaces_double_sided === 1;
        },
        canSubmit() {
            // Require at least one image (front or back) to be uploaded, plus the note.
            const hasAtLeastOneFile = this.frontPreviewUrl || this.backPreviewUrl;
            return !!(hasAtLeastOneFile && this.fileNote);
        }
    },
    methods: {
        closeDialog() {
            this.frontPreviewUrl = '';
            this.backPreviewUrl = '';
            this.frontFile = null;
            this.backFile = null;
            this.fileNote = '';
            this.$emit('update:showDialog', false);
        },

        showPreview(event, side) {
            const input = event.target;

            if (input.files && input.files[0]) {
                const file = input.files[0];
                
                if (side === 'front') {
                    this.frontFile = file;
                } else {
                    this.backFile = file;
                }

                const reader = new FileReader();
                
                reader.onload = e => {
                    if (side === 'front') {
                        this.frontPreviewUrl = e.target.result;
                    } else {
                        this.backPreviewUrl = e.target.result;
                    }
                    input.value = ''; // clear input for re-uploading the same file if needed
                };

                reader.readAsDataURL(file);
            }
        },

        uploadFile() {
            this.loading = true;
            const uploadPromises = [];

            // 1. Queue Front Upload if a file is selected
            if (this.frontFile) {
                uploadPromises.push(
                    this.order.uploadOriginalFile(this.frontFile, this.fileNote, 'front')
                );
            }

            // 2. Queue Back Upload if a file is selected (and it's double-sided)
            if (this.isDoubleSided && this.backFile) {
                uploadPromises.push(
                    this.order.uploadOriginalFile(this.backFile, this.fileNote, 'back')
                );
            }

            // Fallback just in case, though the button should be disabled anyway
            if (uploadPromises.length === 0) {
                this.finishUpload();
                return;
            }

            // 3. Execute the selected uploads simultaneously
            Promise.all(uploadPromises)
                .then(() => {
                    this.$emit('fileUploaded');
                    setTimeout(this.finishUpload, 1000);
                })
                .catch(err => {
                    const apiError = err && err.response && err.response.data && err.response.data.error
                        ? err.response.data.error
                        : null;
                    const message = apiError || (err && err.message) || 'Oooops! Something went wrong. Please, check if your file isn\'t too big.';
                    this.$emit('fileUploadingFailed', message);
                    setTimeout(this.finishUpload, 2500);
                });
        },

        finishUpload() {
            this.loading = false;
            this.closeDialog();
        }
    }
}
</script>

<style lang="scss" scoped>
    .upload-dialog {
        width: 95vw;
        max-width: 1300px; /* Made dialog wider */
        min-width: 560px;
        margin-left: auto;
        margin-right: auto;
        left: 50%;
        transform: translateX(-50%);
        
        .dialog-content-wrapper {
            max-height: 60vh;
            overflow-y: auto;
            padding: 0 2.4rem;
            display: flex;
            flex-direction: column;
            gap: 2.4rem;

            /* If it's double sided, place them side by side on wider screens */
            &.is-double-sided {
                flex-direction: row;
                flex-wrap: wrap;

                .upload-section {
                    flex: 1;
                    min-width: 320px; /* Ensures they stack on smaller screens but sit side-by-side if space allows */
                }
            }
        }

        .upload-section {
            display: flex;
            flex-direction: column;
        }
    
        &__header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 2.4rem;
            padding-bottom: 0.8rem;
            font-weight: bold;
            font-size: 1.1em;

            input[type="file"] {
                display: none;
            }
        }
        &__preview {
            width: 100%;
            min-height: 200px;
            background-color: $color-grey;
            flex-grow: 1; /* Helps keep the height matched if side-by-side */

            .preview-image {
                display: flex;
                max-width: 300px;
                margin: 1rem auto;
                object-fit: contain;
            }
            .md-body-1 {
                color: $color-white;
                text-align: center;
                line-height: 200px;
                font-size: 1.6rem;
            }
        }
        &__note {
            padding: 1.6rem 2.4rem .8rem;
        }
    }
</style>